import React from "react";
import { useSearchParams } from "react-router-dom";
import api, { MEDIA } from "../../lib/api";
import { toast } from "sonner";
import { FileText, Eye, X, Upload, Truck, Plus, Trash2, Receipt, Package, Download, RefreshCw, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { ORDER_STATUS_RO, PAYMENT_STATUS_RO } from "../../lib/statusLabels";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminOrders() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "superadmin";
    const [orders, setOrders] = React.useState([]);
    const [viewing, setViewing] = React.useState(null);
    const [note, setNote] = React.useState("");
    const [statusFilter, setStatusFilter] = React.useState("");
    const [paymentFilter, setPaymentFilter] = React.useState("");
    const [q, setQ] = React.useState("");

    const load = React.useCallback(() => api.get("/admin/orders").then(r => setOrders(r.data || [])), []);
    React.useEffect(() => { load(); }, [load]);

    const [sp] = useSearchParams();
    React.useEffect(() => {
        const openId = sp.get("id") || sp.get("open");
        if (openId) { api.get(`/admin/orders/${openId}`).then(r => setViewing(r.data)).catch(() => {}); }
    }, [sp]);

    const filtered = React.useMemo(() => {
        const needle = q.trim().toLowerCase();
        return orders.filter(o => {
            if (statusFilter && o.status !== statusFilter) return false;
            if (paymentFilter && o.payment_status !== paymentFilter) return false;
            if (needle) {
                const hay = [o.order_number, o.order_id, o.billing?.name, o.billing?.email, o.billing?.phone, o.awb].filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(needle)) return false;
            }
            return true;
        });
    }, [orders, statusFilter, paymentFilter, q]);

    const updateStatus = async (id, status) => { await api.put(`/admin/orders/${id}`, { status }); toast.success("OK"); load(); };
    const openDetail = async (id) => { const res = await api.get(`/admin/orders/${id}`); setViewing(res.data); };
    const refreshView = async () => { if (viewing) { const res = await api.get(`/admin/orders/${viewing.order_id}`); setViewing(res.data); load(); } };

    const setAwb = async (awb, courier, tracking_url) => {
        await api.post(`/admin/orders/${viewing.order_id}/awb`, { awb, courier, tracking_url, mark_shipped: true });
        toast.success("AWB salvat"); refreshView();
    };
    const addNote = async () => {
        if (!note.trim()) return;
        await api.post(`/admin/orders/${viewing.order_id}/notes`, { text: note });
        setNote(""); refreshView();
    };
    const uploadInvoice = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/admin/orders/${viewing.order_id}/invoice-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Factură încărcată"); refreshView();
    };
    const generateInvoice = async () => {
        await api.post(`/admin/orders/${viewing.order_id}/invoice`); toast.success("Număr factură generat"); refreshView();
    };
    const navigate = useNavigate();
    const generateManualInvoice = async (kind = "invoice") => {
        try {
            const r = await api.post(`/admin/invoices/from-order/${viewing.order_id}`, { kind });
            toast.success(kind === "proforma" ? "Proformă creată" : "Factură manuală creată");
            setViewing(null);
            navigate(`/admin/invoices?id=${r.data.invoice_id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare");
        }
    };
    const removeItem = async (idx) => {
        const newItems = viewing.items.filter((_, i) => i !== idx);
        await api.put(`/admin/orders/${viewing.order_id}/items`, { items: newItems, manual_discount: viewing.discount_total || 0, shipping_cost: viewing.shipping_cost || 25 });
        toast.success("Produs eliminat"); refreshView();
    };
    const updateItemQty = async (idx, q) => {
        const newItems = viewing.items.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, q) } : it);
        await api.put(`/admin/orders/${viewing.order_id}/items`, { items: newItems, manual_discount: viewing.discount_total || 0, shipping_cost: viewing.shipping_cost || 25 });
        refreshView();
    };
    const delOrder = async (id) => {
        if (!window.confirm("Sigur ștergi comanda? Acțiunea este ireversibilă.")) return;
        await api.delete(`/admin/orders/${id}`);
        toast.success("Comandă ștearsă"); setViewing(null); load();
    };
    const applyDiscount = async (amt) => {
        await api.put(`/admin/orders/${viewing.order_id}/items`, { items: viewing.items, manual_discount: Number(amt) || 0, shipping_cost: viewing.shipping_cost || 25 });
        toast.success("Discount aplicat"); refreshView();
    };

    const fmt = (n) => `${(n || 0).toFixed(2)} RON`;

    return (
        <div data-testid="admin-orders">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-4">Comenzi</h1>
            <div className="card-soft p-3 mb-4 flex flex-wrap items-center gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută după număr, nume, email, AWB..." className="input-soft !py-2 flex-1 min-w-[220px]" data-testid="orders-search" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-soft !py-2" data-testid="orders-status-filter">
                    <option value="">Toate statusurile</option>
                    {Object.entries(ORDER_STATUS_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} className="input-soft !py-2" data-testid="orders-payment-filter">
                    <option value="">Toate plățile</option>
                    {Object.entries(PAYMENT_STATUS_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <div className="flex items-center gap-2 text-xs text-muted-foreground ml-auto">
                    <span>{filtered.length}/{orders.length}</span>
                    {(statusFilter || paymentFilter || q) && <button onClick={() => { setStatusFilter(""); setPaymentFilter(""); setQ(""); }} className="btn-outline !py-1 text-xs" data-testid="orders-filter-clear">Resetează</button>}
                </div>
            </div>
            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted"><tr>{["Nr.", "Dată", "Client", "Plată", "AWB", "Total", "Status", ""].map(h => <th key={h} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">{h}</th>)}</tr></thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-muted-foreground">Nicio comandă.</td></tr>}
                        {filtered.map((o, i) => (
                            <tr key={o.order_id} className="border-t border-border" data-testid={`order-row-${i}`}>
                                <td className="px-3 py-2 font-mono">{o.order_number || o.order_id.slice(-8)}</td>
                                <td className="px-3 py-2 text-xs">{new Date(o.created_at).toLocaleString("ro-RO")}</td>
                                <td className="px-3 py-2">{o.billing?.name}<br /><span className="text-xs text-muted-foreground">{o.billing?.email}</span></td>
                                <td className="px-3 py-2"><div className={`chip text-[10px] ${o.payment_status === "paid" ? "chip-success" : "bg-muted text-muted-foreground"}`}>{PAYMENT_STATUS_RO[o.payment_status] || o.payment_status}</div><div className="text-[10px] text-muted-foreground mt-1">{o.payment_method}</div></td>
                                <td className="px-3 py-2 text-xs font-mono">{o.awb || "—"}</td>
                                <td className="px-3 py-2 font-medium">{fmt(o.total)}</td>
                                <td className="px-3 py-2">
                                    <select value={o.status} onChange={(e) => updateStatus(o.order_id, e.target.value)} className="input-soft !py-1 text-xs" data-testid={`order-status-${i}`}>
                                        {Object.entries(ORDER_STATUS_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                    </select>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => openDetail(o.order_id)} className="btn-ghost" data-testid={`order-view-${i}`}><Eye className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {viewing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <div>
                                <h2 className="font-heading text-xl">Comandă #{viewing.order_number || viewing.order_id.slice(-8)}</h2>
                                <div className="text-xs text-muted-foreground">{new Date(viewing.created_at).toLocaleString("ro-RO")}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                {isSuperAdmin && (
                                    <button onClick={() => delOrder(viewing.order_id)} className="btn-ghost text-destructive" title="Șterge comandă (superadmin)">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                )}
                                <button onClick={() => setViewing(null)}><X className="h-5 w-5" /></button>
                            </div>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="card-soft p-4"><h4 className="font-medium mb-2">Facturare</h4><div className="text-sm space-y-1 text-muted-foreground"><div>{viewing.billing?.name}</div><div>{viewing.billing?.email}</div><div>{viewing.billing?.phone}</div><div>{viewing.billing?.address}, {viewing.billing?.city}, {viewing.billing?.county}</div></div></div>
                                <div className="card-soft p-4"><h4 className="font-medium mb-2">Livrare</h4><div className="text-sm space-y-1 text-muted-foreground"><div>{viewing.shipping?.name}</div><div>{viewing.shipping?.phone}</div><div>{viewing.shipping?.address}, {viewing.shipping?.city}</div><div>Metodă: <span className="font-medium text-foreground">{viewing.delivery_method}</span></div></div></div>
                            </div>
                            <div>
                                <h4 className="font-medium mb-2">Produse</h4>
                                <div className="card-soft divide-y divide-border overflow-hidden">
                                    {(viewing.items || []).map((it, i) => (
                                        <div key={i} className="flex items-center gap-3 p-3">
                                            <div className="w-14 h-14 bg-muted rounded-lg overflow-hidden flex-shrink-0">{(it.product_image || it.image) && <img src={MEDIA(it.product_image || it.image)} alt="" className="w-full h-full object-cover" />}</div>
                                            <div className="flex-1"><div className="font-medium text-sm">{it.name}</div><div className="text-xs text-muted-foreground">{fmt(it.unit_price)}</div></div>
                                            <input type="number" value={it.quantity} onChange={(e) => updateItemQty(i, Number(e.target.value))} className="input-soft w-16 !py-1" />
                                            <div className="font-medium w-24 text-right">{fmt(it.unit_price * it.quantity)}</div>
                                            <button onClick={() => removeItem(i)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="card-soft p-4 space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmt(viewing.subtotal)}</span></div>
                                <div className="flex justify-between items-center"><span className="text-muted-foreground">Discount manual (RON)</span><input type="number" defaultValue={viewing.discount_total || 0} onBlur={(e) => applyDiscount(e.target.value)} className="input-soft !py-1 w-24 text-right" data-testid="admin-order-discount" /></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Livrare</span><span>{fmt(viewing.shipping_cost)}</span></div>
                                <div className="border-t border-border pt-2 flex justify-between font-heading text-xl"><span>Total</span><span className="text-primary">{fmt(viewing.total)}</span></div>
                            </div>

                            {/* AWB */}
                            <div className="card-soft p-4">
                                <h4 className="font-medium mb-3 flex items-center gap-2"><Truck className="h-4 w-4" /> AWB livrare</h4>
                                <AwbForm current={viewing} onSave={setAwb} onRefresh={refreshView} />
                            </div>

                            {/* Invoice */}
                            <div className="card-soft p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-xs text-muted-foreground">Factură</div>
                                        <div className="font-medium">{viewing.invoice_number || "Nu a fost emisă încă"}</div>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <button onClick={generateInvoice} className="btn-outline !py-2 text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Generează nr.</button>
                                        <button onClick={() => generateManualInvoice("invoice")} className="btn-outline !py-2 text-sm flex items-center gap-2" data-testid="order-gen-manual-invoice"><Receipt className="h-4 w-4" /> Factură manuală</button>
                                        <button onClick={() => generateManualInvoice("proforma")} className="btn-outline !py-2 text-sm flex items-center gap-2" data-testid="order-gen-proforma"><Receipt className="h-4 w-4" /> Proformă</button>
                                        <label className="btn-outline !py-2 text-sm cursor-pointer flex items-center gap-2">
                                            <Upload className="h-4 w-4" /> Încarcă PDF
                                            <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadInvoice(e.target.files[0])} />
                                        </label>
                                        {viewing.invoice_file && <a href={`${BACKEND}${viewing.invoice_file}`} target="_blank" rel="noopener noreferrer" className="btn-primary !py-2 text-sm">Descarcă</a>}
                                    </div>
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="card-soft p-4">
                                <h4 className="font-medium mb-2">Observații</h4>
                                <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                                    {(viewing.notes || []).map((n, i) => (
                                        <div key={i} className="bg-muted rounded-lg p-3 text-sm">
                                            <div className="text-xs text-muted-foreground">{n.author_name} · {new Date(n.created_at).toLocaleString("ro-RO")}</div>
                                            <div>{n.text}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Adaugă observație..." className="input-soft flex-1" data-testid="admin-note-input" />
                                    <button onClick={addNote} className="btn-primary flex items-center gap-1"><Plus className="h-4 w-4" />Adaugă</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function AwbForm({ current, onSave, onRefresh }) {
    const [tab, setTab] = React.useState(
        current.courier === "cargus" ? "cargus" :
        current.courier === "sameday" ? "sameday" :
        current.courier === "fan_courier" || !current.awb ? "fc" : "manual"
    );
    const [awb, setAwb] = React.useState(current.awb || "");
    const [courier, setCourier] = React.useState(current.courier || "fan_courier");
    const [trk, setTrk] = React.useState(current.tracking_url || "");

    // FanCourier generation form
    const [fcForm, setFcForm] = React.useState({
        service: "Standard",
        weight: 1,
        parcels: 1,
        payment: "expeditor",
        cod: current.payment_method === "cod" ? (current.total || 0) : 0,
        declared_value: 0,
        content: (current.items || []).slice(0, 3).map(i => i.name).join(", "),
        observation: `Comanda #${current.order_number || current.order_id?.slice(-8) || ""}`,
        dim_l: 0, dim_h: 0, dim_w: 0,
    });
    const [fcLoading, setFcLoading] = React.useState(false);
    const [tracking, setTracking] = React.useState(null);
    const [trackLoading, setTrackLoading] = React.useState(false);

    // Sameday generation form
    const [sdForm, setSdForm] = React.useState({
        service: 7,
        weight: 1,
        parcel_count: 1,
        cash_on_delivery: current.payment_method === "cod" ? (current.total || 0) : 0,
        insured_value: 0,
        observation: (current.items || []).slice(0, 3).map(i => i.name).join(", "),
        width: 0, length: 0, height: 0,
    });
    const [sdLoading, setSdLoading] = React.useState(false);
    const [sdTracking, setSdTracking] = React.useState(null);
    const [sdTrackLoading, setSdTrackLoading] = React.useState(false);

    // Cargus generation form
    const [cgForm, setCgForm] = React.useState({
        service_id: 34,
        weight: 1,
        parcel_count: 1,
        repayment_value: current.payment_method === "cod" ? (current.total || 0) : 0,
        declared_value: 0,
        open_package: false,
        saturday_delivery: false,
        contents: (current.items || []).slice(0, 3).map(i => i.name).join(", "),
        custom_identifier: `ORD-${current.order_number || current.order_id?.slice(-8) || ""}`,
    });
    const [cgLoading, setCgLoading] = React.useState(false);
    const [cgTracking, setCgTracking] = React.useState(null);
    const [cgTrackLoading, setCgTrackLoading] = React.useState(false);

    const generateFcAwb = async () => {
        setFcLoading(true);
        try {
            const payload = {
                service: fcForm.service,
                weight: parseFloat(fcForm.weight),
                parcels: parseInt(fcForm.parcels),
                payment: fcForm.payment,
                cod: parseFloat(fcForm.cod || 0),
                declared_value: parseFloat(fcForm.declared_value || 0),
                content: fcForm.content,
                observation: fcForm.observation,
            };
            if (fcForm.dim_l || fcForm.dim_h || fcForm.dim_w) {
                payload.dimensions = { length: +fcForm.dim_l, height: +fcForm.dim_h, width: +fcForm.dim_w };
            }
            const r = await api.post(`/admin/fancourier/awb/${current.order_id}`, payload);
            toast.success(`AWB generat: ${r.data.awb}`);
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare generare AWB");
        } finally { setFcLoading(false); }
    };

    const loadTracking = async () => {
        const awbNo = current.awb;
        if (!awbNo) return;
        setTrackLoading(true);
        try {
            const r = await api.get(`/admin/fancourier/awb/${awbNo}/tracking`);
            setTracking(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare tracking");
        } finally { setTrackLoading(false); }
    };

    const downloadLabel = () => {
        if (!current.awb) return;
        const token = localStorage.getItem("st_token");
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/fancourier/awb/${current.awb}/label?pdf=1`, "_blank");
    };

    const deleteAwb = async () => {
        if (!current.awb) return;
        if (!window.confirm(`Anulezi AWB ${current.awb} la FanCourier?`)) return;
        try {
            await api.delete(`/admin/fancourier/awb/${current.awb}`);
            toast.success("AWB anulat");
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare anulare AWB");
        }
    };

    const generateSdAwb = async () => {
        setSdLoading(true);
        try {
            const r = await api.post(`/admin/sameday/awb/${current.order_id}`, {
                service: parseInt(sdForm.service),
                weight: parseFloat(sdForm.weight),
                parcel_count: parseInt(sdForm.parcel_count),
                cash_on_delivery: parseFloat(sdForm.cash_on_delivery || 0),
                insured_value: parseFloat(sdForm.insured_value || 0),
                observation: sdForm.observation,
                ...(sdForm.width  ? { width:  sdForm.width  } : {}),
                ...(sdForm.length ? { length: sdForm.length } : {}),
                ...(sdForm.height ? { height: sdForm.height } : {}),
            });
            toast.success(`AWB Sameday generat: ${r.data.awb}`);
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare generare AWB Sameday");
        } finally { setSdLoading(false); }
    };

    const loadSdTracking = async () => {
        if (!current.awb) return;
        setSdTrackLoading(true);
        try {
            const r = await api.get(`/admin/sameday/awb/${current.awb}/tracking`);
            setSdTracking(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare tracking Sameday");
        } finally { setSdTrackLoading(false); }
    };

    const downloadSdLabel = () => {
        if (!current.awb) return;
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sameday/awb/${current.awb}/label`, "_blank");
    };

    const deleteSdAwb = async () => {
        if (!current.awb) return;
        if (!window.confirm(`Anulezi AWB ${current.awb} la Sameday?`)) return;
        try {
            await api.delete(`/admin/sameday/awb/${current.awb}`);
            toast.success("AWB Sameday anulat");
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare anulare AWB Sameday");
        }
    };

    const generateCgAwb = async () => {
        setCgLoading(true);
        try {
            const payload = {
                service_id: parseInt(cgForm.service_id),
                weight: parseFloat(cgForm.weight),
                parcel_count: parseInt(cgForm.parcel_count),
                repayment_value: parseFloat(cgForm.repayment_value || 0),
                declared_value: parseFloat(cgForm.declared_value || 0),
                open_package: cgForm.open_package,
                saturday_delivery: cgForm.saturday_delivery,
                contents: cgForm.contents,
                custom_identifier: cgForm.custom_identifier,
            };
            const r = await api.post(`/admin/cargus/awb/${current.order_id}`, payload);
            toast.success(`AWB Cargus generat: ${r.data.awb}`);
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare generare AWB Cargus");
        } finally { setCgLoading(false); }
    };

    const loadCgTracking = async () => {
        const awbNo = current.awb;
        if (!awbNo) return;
        setCgTrackLoading(true);
        try {
            const r = await api.get(`/admin/cargus/awb/${awbNo}/tracking`);
            setCgTracking(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare tracking Cargus");
        } finally { setCgTrackLoading(false); }
    };

    const downloadCgLabel = () => {
        if (!current.awb) return;
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cargus/awb/${current.awb}/label`, "_blank");
    };

    const deleteCgAwb = async () => {
        if (!current.awb) return;
        if (!window.confirm(`Anulezi AWB ${current.awb} la Cargus?`)) return;
        try {
            await api.delete(`/admin/cargus/awb/${current.awb}`);
            toast.success("AWB Cargus anulat");
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare anulare AWB Cargus");
        }
    };

    const statusIcon = (id) => {
        if (!id) return null;
        if (id === "S2") return <CheckCircle2 className="h-4 w-4 text-green-500" />;
        if (id?.startsWith("S")) return <Truck className="h-4 w-4 text-blue-500" />;
        if (id?.startsWith("H")) return <Clock className="h-4 w-4 text-amber-500" />;
        return <Package className="h-4 w-4 text-muted-foreground" />;
    };

    return (
        <div className="space-y-3">
            {/* Tab switcher */}
            <div className="flex gap-1 border-b border-border pb-2">
                <button onClick={() => setTab("fc")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === "fc" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                    FAN Courier API
                </button>
                <button onClick={() => setTab("sameday")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === "sameday" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                    Sameday API
                </button>
                <button onClick={() => setTab("cargus")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === "cargus" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                    Cargus API
                </button>
                <button onClick={() => setTab("manual")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === "manual" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                    Manual
                </button>
            </div>

            {/* Current AWB status bar */}
            {current.awb && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="font-mono font-bold text-green-800">{current.awb}</span>
                    <span className="text-green-600 text-xs">({current.courier || "curier"})</span>
                    <div className="ml-auto flex gap-1">
                        {current.courier === "fan_courier" && (
                            <>
                                <button onClick={downloadLabel} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Descarcă etichetă PDF">
                                    <Download className="h-3.5 w-3.5" /> Etichetă
                                </button>
                                <button onClick={loadTracking} disabled={trackLoading} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Refresh tracking">
                                    <RefreshCw className={`h-3.5 w-3.5 ${trackLoading ? "animate-spin" : ""}`} /> Tracking
                                </button>
                                <button onClick={deleteAwb} className="btn-ghost !py-1 !px-2 text-xs text-destructive flex items-center gap-1" title="Anulează AWB">
                                    <X className="h-3.5 w-3.5" /> Anulează
                                </button>
                            </>
                        )}
                        {current.courier === "sameday" && (
                            <>
                                <button onClick={downloadSdLabel} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Descarcă etichetă PDF Sameday">
                                    <Download className="h-3.5 w-3.5" /> Etichetă
                                </button>
                                <button onClick={loadSdTracking} disabled={sdTrackLoading} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Refresh tracking Sameday">
                                    <RefreshCw className={`h-3.5 w-3.5 ${sdTrackLoading ? "animate-spin" : ""}`} /> Tracking
                                </button>
                                <button onClick={deleteSdAwb} className="btn-ghost !py-1 !px-2 text-xs text-destructive flex items-center gap-1" title="Anulează AWB Sameday">
                                    <X className="h-3.5 w-3.5" /> Anulează
                                </button>
                            </>
                        )}
                        {current.courier === "cargus" && (
                            <>
                                <button onClick={downloadCgLabel} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Descarcă etichetă PDF Cargus">
                                    <Download className="h-3.5 w-3.5" /> Etichetă
                                </button>
                                <button onClick={loadCgTracking} disabled={cgTrackLoading} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Refresh tracking Cargus">
                                    <RefreshCw className={`h-3.5 w-3.5 ${cgTrackLoading ? "animate-spin" : ""}`} /> Tracking
                                </button>
                                <button onClick={deleteCgAwb} className="btn-ghost !py-1 !px-2 text-xs text-destructive flex items-center gap-1" title="Anulează AWB Cargus">
                                    <X className="h-3.5 w-3.5" /> Anulează
                                </button>
                            </>
                        )}
                        {current.tracking_url && (
                            <a href={current.tracking_url} target="_blank" rel="noopener noreferrer"
                               className="btn-ghost !py-1 !px-2 text-xs text-primary">
                                Track →
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Tracking events */}
            {tracking && (
                <div className="border border-border rounded-lg overflow-hidden text-xs">
                    <div className="bg-muted px-3 py-2 font-medium text-xs flex items-center justify-between">
                        <span>Tracking AWB {tracking.awbNumber}</span>
                        {tracking.confirmation?.date && (
                            <span className="text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Livrat {tracking.confirmation.date}
                            </span>
                        )}
                    </div>
                    <div className="divide-y divide-border max-h-48 overflow-y-auto">
                        {(tracking.events || []).map((ev, i) => (
                            <div key={i} className="flex items-start gap-2 px-3 py-2">
                                {statusIcon(ev.id)}
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium">{ev.name}</div>
                                    <div className="text-muted-foreground">{ev.location} · {ev.date}</div>
                                </div>
                                <span className="font-mono text-[10px] text-muted-foreground">{ev.id}</span>
                            </div>
                        ))}
                        {(!tracking.events || tracking.events.length === 0) && (
                            <div className="px-3 py-4 text-center text-muted-foreground">Niciun eveniment înregistrat</div>
                        )}
                    </div>
                </div>
            )}

            {/* FanCourier generation form */}
            {tab === "fc" && (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Serviciu</label>
                            <select value={fcForm.service} onChange={e => setFcForm(f => ({...f, service: e.target.value}))} className="input-soft !py-2 text-xs">
                                <option>Standard</option>
                                <option>Cont Colector</option>
                                <option>Plicuri</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Greutate (kg)</label>
                            <input type="number" min="0.1" step="0.1" value={fcForm.weight}
                                onChange={e => setFcForm(f => ({...f, weight: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Colete</label>
                            <input type="number" min="1" value={fcForm.parcels}
                                onChange={e => setFcForm(f => ({...f, parcels: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Plată transport</label>
                            <select value={fcForm.payment} onChange={e => setFcForm(f => ({...f, payment: e.target.value}))} className="input-soft !py-2 text-xs">
                                <option value="expeditor">Expeditor (noi plătim)</option>
                                <option value="destinatar">Destinatar</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Ramburs (RON)</label>
                            <input type="number" min="0" step="0.01" value={fcForm.cod}
                                onChange={e => setFcForm(f => ({...f, cod: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Valoare declarată (RON)</label>
                            <input type="number" min="0" step="0.01" value={fcForm.declared_value}
                                onChange={e => setFcForm(f => ({...f, declared_value: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                    </div>

                    {/* Dimensions (optional) */}
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Dimensiuni cm — L × H × W (opțional)</label>
                        <div className="grid grid-cols-3 gap-2">
                            {["dim_l", "dim_h", "dim_w"].map((k, i) => (
                                <input key={k} type="number" min="0" placeholder={["Lungime","Înălțime","Lățime"][i]}
                                    value={fcForm[k]} onChange={e => setFcForm(f => ({...f, [k]: e.target.value}))}
                                    className="input-soft !py-2 text-xs" />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Conținut colet</label>
                        <input value={fcForm.content} onChange={e => setFcForm(f => ({...f, content: e.target.value}))} className="input-soft !py-2 text-xs" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Observații</label>
                        <input value={fcForm.observation} onChange={e => setFcForm(f => ({...f, observation: e.target.value}))} className="input-soft !py-2 text-xs" />
                    </div>

                    {/* Recipient preview */}
                    <div className="bg-muted/50 rounded-lg px-3 py-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Destinatar: </span>
                        {current.billing?.name} · {current.billing?.phone} · {current.billing?.address?.city}, {current.billing?.address?.county}
                    </div>

                    <button onClick={generateFcAwb} disabled={fcLoading}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                        data-testid="fc-generate-awb">
                        {fcLoading ? <><RefreshCw className="h-4 w-4 animate-spin" /> Se generează...</> : <><Package className="h-4 w-4" /> Generează AWB FAN Courier</>}
                    </button>
                </div>
            )}

            {/* Sameday tracking events */}
            {sdTracking && (
                <div className="border border-border rounded-lg overflow-hidden text-xs">
                    <div className="bg-muted px-3 py-2 font-medium text-xs flex items-center justify-between">
                        <span>Tracking Sameday {current.awb}</span>
                        {sdTracking.deliveredAt && (
                            <span className="text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Livrat {sdTracking.deliveredAt}
                            </span>
                        )}
                    </div>
                    <div className="divide-y divide-border max-h-48 overflow-y-auto">
                        {(sdTracking.history || sdTracking.statusHistory || []).length > 0
                            ? (sdTracking.history || sdTracking.statusHistory).map((ev, i) => (
                                <div key={i} className="flex items-start gap-2 px-3 py-2">
                                    <Package className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium">{ev.label || ev.statusLabel || ev.status || JSON.stringify(ev)}</div>
                                        <div className="text-muted-foreground">{ev.county || ev.city || ""}{ev.transitLocation ? ` · ${ev.transitLocation}` : ""} · {ev.statusDate || ev.date || ""}</div>
                                    </div>
                                </div>
                            ))
                            : <div className="px-3 py-4 text-center text-muted-foreground">Niciun eveniment înregistrat</div>
                        }
                    </div>
                </div>
            )}

            {/* Sameday generation form */}
            {tab === "sameday" && (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Serviciu</label>
                            <select value={sdForm.service} onChange={e => setSdForm(f => ({...f, service: e.target.value}))} className="input-soft !py-2 text-xs">
                                <option value={7}>24H (Standard)</option>
                                <option value={10}>Retur Standard</option>
                                <option value={15}>Locker Nextday</option>
                                <option value={24}>Locker Retur</option>
                                <option value={28}>Crossborder HD 24H</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Greutate totală (kg)</label>
                            <input type="number" min="0.1" step="0.1" value={sdForm.weight}
                                onChange={e => setSdForm(f => ({...f, weight: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Nr. colete</label>
                            <input type="number" min="1" value={sdForm.parcel_count}
                                onChange={e => setSdForm(f => ({...f, parcel_count: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Ramburs (RON)</label>
                            <input type="number" min="0" step="0.01" value={sdForm.cash_on_delivery}
                                onChange={e => setSdForm(f => ({...f, cash_on_delivery: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Valoare asigurată (RON)</label>
                            <input type="number" min="0" step="0.01" value={sdForm.insured_value}
                                onChange={e => setSdForm(f => ({...f, insured_value: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Dimensiuni cm — L × H × W (opțional)</label>
                        <div className="grid grid-cols-3 gap-2">
                            {[["length","Lungime"],["height","Înălțime"],["width","Lățime"]].map(([k, lbl]) => (
                                <input key={k} type="number" min="0" placeholder={lbl}
                                    value={sdForm[k]} onChange={e => setSdForm(f => ({...f, [k]: e.target.value}))}
                                    className="input-soft !py-2 text-xs" />
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Observații / conținut</label>
                        <input value={sdForm.observation} onChange={e => setSdForm(f => ({...f, observation: e.target.value}))} className="input-soft !py-2 text-xs" />
                    </div>
                    <div className="bg-muted/50 rounded-lg px-3 py-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Destinatar: </span>
                        {current.billing?.name} · {current.billing?.phone} · {current.billing?.address?.city || current.billing?.city}, {current.billing?.address?.county || current.billing?.county}
                    </div>
                    <button onClick={generateSdAwb} disabled={sdLoading}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                        data-testid="sd-generate-awb">
                        {sdLoading ? <><RefreshCw className="h-4 w-4 animate-spin" /> Se generează...</> : <><Package className="h-4 w-4" /> Generează AWB Sameday</>}
                    </button>
                </div>
            )}

            {/* Cargus tracking events */}
            {cgTracking && (
                <div className="border border-border rounded-lg overflow-hidden text-xs">
                    <div className="bg-muted px-3 py-2 font-medium text-xs flex items-center justify-between">
                        <span>Tracking Cargus {current.awb}</span>
                    </div>
                    <div className="divide-y divide-border max-h-48 overflow-y-auto">
                        {Array.isArray(cgTracking) && cgTracking.length > 0 ? cgTracking.map((ev, i) => (
                            <div key={i} className="flex items-start gap-2 px-3 py-2">
                                <Package className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium">{ev.Event || ev.Status || ev.Description || JSON.stringify(ev)}</div>
                                    <div className="text-muted-foreground">{ev.Date || ev.Timestamp || ""}</div>
                                </div>
                            </div>
                        )) : (
                            <div className="px-3 py-4 text-center text-muted-foreground">Niciun eveniment înregistrat</div>
                        )}
                    </div>
                </div>
            )}

            {/* Cargus generation form */}
            {tab === "cargus" && (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Service ID</label>
                            <input type="number" min="1" value={cgForm.service_id}
                                onChange={e => setCgForm(f => ({...f, service_id: e.target.value}))}
                                className="input-soft !py-2 text-xs" placeholder="ex: 34" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Greutate (kg)</label>
                            <input type="number" min="0.1" step="0.1" value={cgForm.weight}
                                onChange={e => setCgForm(f => ({...f, weight: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Nr. colete</label>
                            <input type="number" min="1" value={cgForm.parcel_count}
                                onChange={e => setCgForm(f => ({...f, parcel_count: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Ramburs (RON)</label>
                            <input type="number" min="0" step="0.01" value={cgForm.repayment_value}
                                onChange={e => setCgForm(f => ({...f, repayment_value: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Valoare declarată (RON)</label>
                            <input type="number" min="0" step="0.01" value={cgForm.declared_value}
                                onChange={e => setCgForm(f => ({...f, declared_value: e.target.value}))}
                                className="input-soft !py-2 text-xs" />
                        </div>
                        <div className="flex flex-col gap-1 justify-end pb-1">
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={cgForm.open_package}
                                    onChange={e => setCgForm(f => ({...f, open_package: e.target.checked}))} />
                                Deschis la livrare
                            </label>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={cgForm.saturday_delivery}
                                    onChange={e => setCgForm(f => ({...f, saturday_delivery: e.target.checked}))} />
                                Livrare sâmbătă
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Conținut colet</label>
                        <input value={cgForm.contents} onChange={e => setCgForm(f => ({...f, contents: e.target.value}))} className="input-soft !py-2 text-xs" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Identificator custom</label>
                        <input value={cgForm.custom_identifier} onChange={e => setCgForm(f => ({...f, custom_identifier: e.target.value}))} className="input-soft !py-2 text-xs" />
                    </div>
                    <div className="bg-muted/50 rounded-lg px-3 py-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Destinatar: </span>
                        {current.billing?.name} · {current.billing?.phone} · {current.billing?.address?.city}, {current.billing?.address?.county}
                    </div>
                    <button onClick={generateCgAwb} disabled={cgLoading}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                        data-testid="cg-generate-awb">
                        {cgLoading ? <><RefreshCw className="h-4 w-4 animate-spin" /> Se generează...</> : <><Package className="h-4 w-4" /> Generează AWB Cargus</>}
                    </button>
                </div>
            )}

            {/* Manual form */}
            {tab === "manual" && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <input value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="Număr AWB" className="input-soft" data-testid="awb-input" />
                    <select value={courier} onChange={(e) => setCourier(e.target.value)} className="input-soft">
                        <option value="fan_courier">Fan Courier</option>
                        <option value="sameday">Sameday</option>
                        <option value="cargus">Cargus</option>
                        <option value="gls">GLS</option>
                        <option value="dpd">DPD</option>
                        <option value="manual">Manual</option>
                    </select>
                    <input value={trk} onChange={(e) => setTrk(e.target.value)} placeholder="URL tracking (opțional)" className="input-soft" />
                    <button onClick={() => onSave(awb, courier, trk)} className="btn-primary" data-testid="awb-save">Salvează + Expediat</button>
                </div>
            )}
        </div>
    );
}
